import { useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { FormsQueryKeys, FormsWritebacks, FormsHashUtils } from '@frontend/api-forms';
import { generateQueryKey } from '../../utils';
import { useDigitalFormsLocationsContext } from '../forms-locations';
import { useWritebackCapabilitiesStore } from './store';
import { WritebackCapabilitiesStore, SourceIdToLocationIdsAssociation, GetLocationIdsWithSourceIdFn } from './types';
import { WritebackCapabilitiesUtils } from './utils';

const { checkSettingTypeCapability, associateSourceIdToLocationIdsForMedicalConditions } = WritebackCapabilitiesUtils;

type GetSourceIdsCapableOfMedicalConditionForLocationFn = (locationId: string) => string[];
type GetSourceDataCapableOfMedicalConditionForSelectedLocationsFn = (
  locationIds?: string[]
) => SourceIdToLocationIdsAssociation[];

export type UseWritebackCapabilitiesResults = Pick<
  WritebackCapabilitiesStore,
  'hasFetchedWritebackCapabilities' | 'isLoadingWritebackCapabilities' | 'isFetchingWritebackCapabilities'
> & {
  /**
   * Returns the writeback capabilities for a given sourceId.
   * NOTE: This takes into account the sourceId,
   * and not the source tenant ID.
   */
  getSourceCapabilities: (locationId: string, sourceId: string) => FormsWritebacks.Types.WritebackCapabilities;

  /**
   * NOTE: This takes into account the sourceId,
   * and not the source tenant ID.
   */
  isWritebackCapableSource: (locationId: string, sourceId: string) => boolean;

  hasWritebackCapableSources: (locationId: string) => boolean;

  /**
   * Returns the sourceIds capable of medical condition for a given location.
   */
  getSourceIdsCapableOfMedicalConditionForLocation: GetSourceIdsCapableOfMedicalConditionForLocationFn;

  /**
   * Returns the source ID -> Location ID data capable of medical condition
   * for the selected locations in the GLS (by default), or for the given locationIds.
   */
  getSourceDataCapableOfMedicalConditionForSelectedLocations: GetSourceDataCapableOfMedicalConditionForSelectedLocationsFn;

  /**
   * Returns the locationIds with the given sourceId.
   */
  getLocationIdsWithSourceId: GetLocationIdsWithSourceIdFn;
};

export const useWritebackCapabilities = (): UseWritebackCapabilitiesResults => {
  const queryClient = useQueryClient();
  const { isValidatingLocations, validFormsLocations } = useDigitalFormsLocationsContext();
  const {
    hasFetchedWritebackCapabilities,
    isLoadingWritebackCapabilities,
    isFetchingWritebackCapabilities,
    setIsLoadingWritebackCapabilities,
    setIsFetchingWritebackCapabilities,
    setHasFetchedWritebackCapabilities,
    currentHash,
    setWritebackCapabilities,
    setCurrentHash,
    writebackCapabilities,
  } = useWritebackCapabilitiesStore([
    'hasFetchedWritebackCapabilities',
    'isLoadingWritebackCapabilities',
    'isFetchingWritebackCapabilities',
    'setIsLoadingWritebackCapabilities',
    'setIsFetchingWritebackCapabilities',
    'setHasFetchedWritebackCapabilities',
    'currentHash',
    'setWritebackCapabilities',
    'setCurrentHash',
    'writebackCapabilities',
  ]);

  const invalidateCache = useCallback(
    (hash: string, skipIfSameLocation = false) => {
      if (!hash) {
        return Promise.resolve();
      }

      const locIds = FormsHashUtils.getLocationIdsFromHash(hash);
      if (skipIfSameLocation) {
        if (validFormsLocations.sort().join('') === locIds.sort().join('')) {
          return Promise.resolve();
        }
      }

      const queryKey = generateQueryKey(FormsQueryKeys.writebackCapabilitiesV3, locIds);
      return queryClient.invalidateQueries(queryKey);
    },
    [queryClient, validFormsLocations]
  );

  const {
    data: writebackCapabilitiesResponses,
    isFetching: isFetchingWritebackCapabilitiesResponses,
    isLoading: isLoadingWritebackCapabilitiesResponses,
    isError: isErrorLoadingWritebackCapabilitiesResponses,
    isStale,
  } = useQuery(
    generateQueryKey(FormsQueryKeys.writebackCapabilitiesV3, validFormsLocations),
    () => FormsWritebacks.API_V3.getWritebackCapabilitiesForLocations(validFormsLocations),
    {
      enabled: !isValidatingLocations && validFormsLocations.length > 0,
    }
  );

  useEffect(() => {
    setIsLoadingWritebackCapabilities(isLoadingWritebackCapabilitiesResponses);

    if (isLoadingWritebackCapabilitiesResponses) {
      setHasFetchedWritebackCapabilities(false);
    }
  }, [isLoadingWritebackCapabilitiesResponses]);

  useEffect(() => {
    setIsFetchingWritebackCapabilities(isFetchingWritebackCapabilitiesResponses);
  }, [isFetchingWritebackCapabilitiesResponses]);

  // Update the store with the fetched data
  useEffect(() => {
    if (
      !writebackCapabilitiesResponses ||
      isLoadingWritebackCapabilitiesResponses ||
      isErrorLoadingWritebackCapabilitiesResponses ||
      isFetchingWritebackCapabilitiesResponses ||
      isStale ||
      currentHash === writebackCapabilitiesResponses.hash
    ) {
      return;
    }

    // Invalidate the previous location(s) query cache when locations change
    if (currentHash && currentHash !== writebackCapabilitiesResponses.hash) {
      invalidateCache(currentHash, true);
    }

    setWritebackCapabilities(writebackCapabilitiesResponses.data);
    setCurrentHash(writebackCapabilitiesResponses.hash);
    setHasFetchedWritebackCapabilities(true);
  }, [
    writebackCapabilitiesResponses,
    isLoadingWritebackCapabilitiesResponses,
    isErrorLoadingWritebackCapabilitiesResponses,
    isFetchingWritebackCapabilitiesResponses,
    isStale,
    currentHash,
    invalidateCache,
  ]);

  const getSourceCapabilities = useCallback(
    (locationId: string, sourceId: string) => {
      const wbSources = writebackCapabilities[locationId] ?? [];
      const wbSource = wbSources.find((source) => source.sourceId === sourceId);
      const capabilities = wbSource?.capabilities ?? [];

      return {
        showMedicalConditionPrimaryFields: checkSettingTypeCapability(capabilities, 'medical_condition_primary_fields'),
        showWritebackSettings: {
          createPerson: checkSettingTypeCapability(capabilities, 'writeback_create_person'),
          updatePerson: checkSettingTypeCapability(capabilities, 'writeback_update_person'),
          uploadDocument: checkSettingTypeCapability(capabilities, 'writeback_upload_document'),
        },
        showMedicalConditionSettings: checkSettingTypeCapability(capabilities, 'medical_condition_tab'),
        showWritebackSubmissionsTab: checkSettingTypeCapability(capabilities, 'writeback_submission_tabs'),
        showNpPatientWriteback: checkSettingTypeCapability(capabilities, 'np_patient_writeback'),
      };
    },
    [writebackCapabilities]
  );

  const isWritebackCapableSource = useCallback(
    (locationId: string, sourceId: string) => {
      const wbSources = writebackCapabilities[locationId] ?? [];
      const index = wbSources.findIndex((source) => source.sourceId === sourceId);

      return index !== -1;
    },
    [writebackCapabilities]
  );

  const hasWritebackCapableSources = useCallback(
    (locationId: string) => {
      const wbSources = writebackCapabilities[locationId] ?? [];
      return wbSources.length > 0;
    },
    [writebackCapabilities]
  );

  const getSourceIdsCapableOfMedicalConditionForLocation =
    useCallback<GetSourceIdsCapableOfMedicalConditionForLocationFn>(
      (locationId) => {
        const sourceIds: string[] = [];
        const sources = writebackCapabilities[locationId] ?? [];
        sources.forEach((source) => {
          if (
            source.capabilities.some(
              (capability) => capability.settingType === 'medical_condition_tab' && capability.isCapable
            )
          ) {
            sourceIds.push(source.sourceId);
          }
        });

        return sourceIds;
      },
      [writebackCapabilities]
    );

  const getSourceDataCapableOfMedicalConditionForSelectedLocations =
    useCallback<GetSourceDataCapableOfMedicalConditionForSelectedLocationsFn>(
      (locationIds) => {
        const sourceIdToLocationIdsAssociation: SourceIdToLocationIdsAssociation[] = [];
        if (locationIds) {
          for (const locationId of locationIds) {
            const locationCapabilities = writebackCapabilities[locationId];
            if (!locationCapabilities) {
              continue;
            }

            associateSourceIdToLocationIdsForMedicalConditions({
              associationRef: sourceIdToLocationIdsAssociation,
              capabilities: locationCapabilities,
              locationId,
            });
          }
        } else {
          for (const locationId in writebackCapabilities) {
            const locationCapabilities = writebackCapabilities[locationId];
            if (!locationCapabilities) {
              continue;
            }

            associateSourceIdToLocationIdsForMedicalConditions({
              associationRef: sourceIdToLocationIdsAssociation,
              capabilities: locationCapabilities,
              locationId,
            });
          }
        }

        return sourceIdToLocationIdsAssociation;
      },
      [writebackCapabilities]
    );

  const getLocationIdsWithSourceId = useCallback<GetLocationIdsWithSourceIdFn>(
    (sourceId) => {
      const locationIds: string[] = [];
      for (const locationId in writebackCapabilities) {
        const sources = writebackCapabilities[locationId];
        const found = sources.find((source) => source.sourceId === sourceId);
        if (found && !locationIds.includes(locationId)) {
          locationIds.push(locationId);
        }
      }

      return locationIds;
    },
    [writebackCapabilities]
  );

  const results = useMemo<UseWritebackCapabilitiesResults>(
    () => ({
      hasFetchedWritebackCapabilities,
      isLoadingWritebackCapabilities,
      isFetchingWritebackCapabilities,
      getSourceCapabilities,
      isWritebackCapableSource,
      hasWritebackCapableSources,
      getSourceIdsCapableOfMedicalConditionForLocation,
      getSourceDataCapableOfMedicalConditionForSelectedLocations:
        getSourceDataCapableOfMedicalConditionForSelectedLocations,
      getLocationIdsWithSourceId,
    }),
    [
      hasFetchedWritebackCapabilities,
      isLoadingWritebackCapabilities,
      isFetchingWritebackCapabilities,
      getSourceCapabilities,
      isWritebackCapableSource,
      hasWritebackCapableSources,
      getSourceIdsCapableOfMedicalConditionForLocation,
      getSourceDataCapableOfMedicalConditionForSelectedLocations,
      getLocationIdsWithSourceId,
    ]
  );

  return results;
};
